import * as React from "react";
import { Box, IconButton, Tooltip } from "@mui/material";
import { DownloadIcon, VisibilityIcon, OpenInNewIcon } from "../../../icons";
import { FileIcon } from "../../../common/components/FileIcon";
import { downloadWithAuth, fetchFileBlob, FileDownloadOptions } from "../../../common/utils/downloadWithAuth";
import { ExcelViewer } from "./ExcelViewer";
import { PDFViewerDialog } from "../../../common/components/PDFViewerDialog";
import { useRootStore } from "../../../store/contexts";
import { useProjectFileAffordances } from "../../../common/files/projectFileAffordances";

// Spreadsheets the browser cannot render inline; viewed locally with SheetJS.
const EXCEL_MIMETYPES = new Set([
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // .xlsx
  "application/vnd.ms-excel",                                          // .xls
  "application/vnd.ms-excel.sheet.macroEnabled.12",                    // .xlsm
]);

export type FileCellAffordancesOptions = {
  /** Entity name of the rows. Used to resolve the live download endpoint
   *  `/<entityName>.<fileFieldName>/<row.id>/<filename>` when no snapshot
   *  destination is in play. */
  entityName?: string;
  /** Set together with `snapshotRecordId` when the table lives inside a
   *  snapshot view. Files are addressed by their uuid via the snapshot
   *  endpoint `/<snapshotEntityName>.data/<snapshotRecordId>/<uuid>`. */
  snapshotEntityName?: string;
  snapshotRecordId?: string;
  /** Show the Download IconButton in cells. The View affordance is implicit
   *  for PDFs and Excel files (browser opens or SheetJS renders). */
  enableDownload?: boolean;
};

export type FileCellRenderer = (
  row: any,
  fileFieldName: string,
  mimetype: string,
  filename: string,
) => React.ReactNode;

export type FileCellAffordances = {
  /** Dibuja la celda de un archivo: su icono, su nombre y los botones que
   *  correspondan (Ver para PDF, imagen y Excel; el de OpenTimestamps para un
   *  .ots; los iconos propios del proyecto, de
   *  src/common/files/projectFileAffordances.tsx; Descargar si
   *  `enableDownload`). Sin endpoint (ni `entityName` ni `snapshotEntityName`)
   *  no hay Ver, ni Descargar, ni iconos propios. */
  renderFileCell: FileCellRenderer;
  /** Para montar en el JSX del consumidor (por lo general al final): tiene
   *  los visores que abre Ver (ExcelViewer y PDFViewerDialog) y el `element`
   *  del asiento del proyecto (lo que sus iconos dibujan fuera de la celda,
   *  por ejemplo un diálogo). */
  excelViewerElement: React.ReactNode;
};

/**
 * Shared file-cell behaviour for table primitives (ShowItemsCompact,
 * ItemsCRUDCompact, …). Wires download / view-PDF / view-Excel through one
 * auth path and renders a uniform cell with icon affordances. Each consumer
 * just decides where to call `renderFileCell` inside its Cell renderer and
 * where to mount `excelViewerElement`.
 */
export function useFileCellAffordances(
  opts: FileCellAffordancesOptions,
): FileCellAffordances {
  const { entityName, snapshotEntityName, snapshotRecordId, enableDownload = false } = opts;
  const rootStore = useRootStore();
  // Los iconos propios del proyecto: el asiento se llama siempre, una vez, como
  // todo hook.
  const { affordancesFor, element: projectElement } = useProjectFileAffordances();

  const resolveOptions = React.useCallback(
    (row: any, fileFieldName: string, inline: boolean): FileDownloadOptions | null => {
      const fileData = row[`${fileFieldName}_data`];
      const fileName = row[fileFieldName];
      if (!fileData || !fileName) return null;
      if (snapshotEntityName && snapshotRecordId && fileData.uuid) {
        return {
          entityName: snapshotEntityName,
          fieldName: "data",
          instanceId: snapshotRecordId,
          fileName: fileData.uuid,
          localFileName: fileName,
          inline,
        };
      }
      if (!entityName) return null;
      return {
        entityName,
        fieldName: fileFieldName,
        instanceId: row.id,
        fileName,
        inline,
      };
    },
    [entityName, snapshotEntityName, snapshotRecordId],
  );

  const handleDownload = React.useCallback(
    (row: any, fileFieldName: string) => {
      const options = resolveOptions(row, fileFieldName, false);
      if (!options) return;
      downloadWithAuth(options, undefined, rootStore.activityStore).catch((err: any) => {
        console.error("Error downloading file:", err);
      });
    },
    [resolveOptions, rootStore],
  );

  // Ver PDF/imagen: modal in-app (PDFViewerDialog con blob URL). Antes se
  // abría pestaña nueva (window.open) — en modo PWA sacaba al usuario de la
  // app. El blob URL se revoca al cerrar.
  const [pdfView, setPdfView] = React.useState<{ url: string; title: string } | null>(null);

  const handleViewModal = React.useCallback(
    (row: any, fileFieldName: string) => {
      const options = resolveOptions(row, fileFieldName, true);
      if (!options) return;
      fetchFileBlob(options, rootStore.activityStore)
        .then((blob) => {
          if (!blob) return;
          setPdfView({
            url: window.URL.createObjectURL(blob),
            title: row[fileFieldName] || "Documento",
          });
        })
        .catch((err: any) => {
          console.error("Error opening file:", err);
        });
    },
    [resolveOptions, rootStore],
  );

  const closePdfView = React.useCallback(() => {
    setPdfView((prev) => {
      if (prev) window.URL.revokeObjectURL(prev.url);
      return null;
    });
  }, []);

  const [excelView, setExcelView] = React.useState<{ blob: Blob; filename: string } | null>(null);

  const handleViewExcel = React.useCallback(
    (row: any, fileFieldName: string) => {
      const options = resolveOptions(row, fileFieldName, false);
      if (!options) return;
      fetchFileBlob(options, rootStore.activityStore)
        .then((blob) => {
          if (!blob) return;
          setExcelView({ blob, filename: row[fileFieldName] || "Excel" });
        })
        .catch((err: any) => {
          console.error("Error fetching Excel file:", err);
        });
    },
    [resolveOptions, rootStore],
  );

  const renderFileCell = React.useCallback<FileCellRenderer>(
    (row, fileFieldName, mimetype, filename) => {
      const mimetypeLower = typeof mimetype === "string" ? mimetype.toLowerCase() : "";
      const isPdf = mimetypeLower === "application/pdf";
      const isImage = mimetypeLower.startsWith("image/");
      const isExcel = EXCEL_MIMETYPES.has(mimetypeLower);
      // El .ots (proof OpenTimestamps del anclaje blockchain) no se "ve" como un
      // PDF: es una prueba binaria. Su afordancia es verificarlo en el
      // verificador publico de OpenTimestamps.
      const isOts = typeof filename === "string" && filename.toLowerCase().endsWith(".ots");
      const canView = isPdf || isImage || isExcel;
      const endpointAvailable = !!(entityName || snapshotEntityName);
      // Un icono propio recibe cómo bajar el archivo (las opciones de
      // Descargar): sin endpoint, o si no se pueden resolver, no hay. En una
      // tabla de snapshot, solo por el snapshot (con el uuid del archivo y el
      // registro): sin ellos resolveOptions cae a la ruta del registro vivo con
      // el índice de la fila como id, que no existe. Ver y Descargar no miran
      // esto; lo de ellos viene de antes.
      const viaSnapshot = !snapshotEntityName || !!(snapshotRecordId && row[`${fileFieldName}_data`]?.uuid);
      const download =
        filename && endpointAvailable && viaSnapshot ? resolveOptions(row, fileFieldName, false) : null;
      const projectAffordances = download
        ? affordancesFor({
            row,
            fileFieldName,
            mimetype,
            filename,
            download,
            entityName,
            snapshotEntityName,
            snapshotRecordId,
          })
        : [];
      return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
          <FileIcon mimetype={mimetype} filename={filename} size={24} />
          <Box
            component="span"
            sx={{
              flexGrow: 1,
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            title={filename}
          >
            {filename}
          </Box>
          {canView && filename && endpointAvailable && (
            <Tooltip title="Ver" placement="top" arrow>
              <IconButton
                size="small"
                sx={{ flexShrink: 0 }}
                onClick={(e) => {
                  e.stopPropagation();
                  if (isExcel) handleViewExcel(row, fileFieldName);
                  else handleViewModal(row, fileFieldName);
                }}
              >
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {isOts && (
            <Tooltip title="Verificar en OpenTimestamps" placement="top" arrow>
              <IconButton
                size="small"
                sx={{ flexShrink: 0 }}
                onClick={(e) => {
                  e.stopPropagation();
                  // A un sitio externo a proposito (a diferencia del PDF, que abre
                  // in-app): el verificador publico de OpenTimestamps es la
                  // herramienta estandar. El usuario sube el .ots (boton de
                  // descargar, al lado) y ve el anclaje en la blockchain.
                  window.open("https://opentimestamps.org/", "_blank", "noopener,noreferrer");
                }}
              >
                <OpenInNewIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {projectAffordances.map((affordance) => (
            <Tooltip key={affordance.key} title={affordance.title} placement="top" arrow>
              <IconButton
                size="small"
                sx={{ flexShrink: 0 }}
                onClick={(e) => {
                  e.stopPropagation();
                  affordance.onClick();
                }}
              >
                {affordance.icon}
              </IconButton>
            </Tooltip>
          ))}
          {enableDownload && filename && endpointAvailable && (
            <Tooltip title="Descargar" placement="top" arrow>
              <IconButton
                size="small"
                sx={{ flexShrink: 0 }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleDownload(row, fileFieldName);
                }}
              >
                <DownloadIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      );
    },
    [
      enableDownload,
      entityName,
      snapshotEntityName,
      snapshotRecordId,
      resolveOptions,
      affordancesFor,
      handleDownload,
      handleViewExcel,
      handleViewModal,
    ],
  );

  // Un solo elemento a montar por el consumidor: incluye ambos visores
  // (Excel y PDF/imagen) y el elemento del asiento del proyecto — los
  // consumidores existentes no cambian.
  const excelViewerElement = (
    <>
      <ExcelViewer
        open={excelView !== null}
        onClose={() => setExcelView(null)}
        blob={excelView?.blob ?? null}
        filename={excelView?.filename}
      />
      <PDFViewerDialog
        open={pdfView !== null}
        onClose={closePdfView}
        pdfUrl={pdfView?.url ?? ""}
        title={pdfView?.title}
      />
      {projectElement}
    </>
  );

  return { renderFileCell, excelViewerElement };
}
